import axios from 'axios';
import { IS_NATIVE_APP } from '../config/api';
import { normalizeAthenaApiFailure } from '../utils/athenaApiFailure';
import { generateImage } from '../utils/athenaImageEngine';
import { parseCalendarChatRequest, defaultReminderMinutesFor } from '../utils/calendarChat';
import { parseTaskChatRequest, reminderMinutesForTask } from '../utils/taskChat';
import { persistOperationalChat, reminderPresentation, taskPresentation } from '../utils/operationalChat';
import {
  captureAthenaPhoto,
  createNativeCalendarEvent,
  deleteNativeCalendarEvent,
  getNativeCalendars,
  listNativeCalendarEvents,
  requestNativeCalendarAccess,
  uploadAthenaContextItem,
  updateNativeCalendarEvent,
} from './AthenaCapabilities';
import {
  cancelAthenaNativeAlarm,
  getAthenaNativeAlarms,
  scheduleAthenaAlarmForReminder,
} from '../notifications/AthenaNotifications';
import { openExternal } from '../platform/NativeBridge';

/**
 * Single operational boundary for Home, Workspace and Quick Athena.
 *
 * A model may propose an intent in the future, but prose is never execution
 * evidence. Every branch below returns the same ActionResult contract and
 * only confirmation text derived from a completed provider result.
 */
export const ATHENA_OPERATIONAL_ACTION_TYPES = Object.freeze({
  CREATE_ALARM: 'CREATE_ALARM',
  CREATE_REMINDER: 'CREATE_REMINDER',
  CREATE_CALENDAR_EVENT: 'CREATE_CALENDAR_EVENT',
  UPDATE_CALENDAR_EVENT: 'UPDATE_CALENDAR_EVENT',
  CREATE_TASK: 'CREATE_TASK',
  ASSIGN_TASK_TO_PROJECT: 'ASSIGN_TASK_TO_PROJECT',
  CREATE_PROJECT: 'CREATE_PROJECT',
  CREATE_ROUTINE: 'CREATE_ROUTINE',
  UPDATE_ROUTINE: 'UPDATE_ROUTINE',
  REORGANIZE_DAY: 'REORGANIZE_DAY',
  CONFIRM_DAY_PROPOSAL: 'CONFIRM_DAY_PROPOSAL',
  SAVE_TO_INBOX: 'SAVE_TO_INBOX',
  SAVE_PROJECT_DECISION: 'SAVE_PROJECT_DECISION',
  SAVE_CONTENT: 'SAVE_CONTENT',
  TAKE_PHOTO: 'TAKE_PHOTO',
  OPEN_EXTERNAL: 'OPEN_EXTERNAL',
  GENERATE_IMAGE: 'GENERATE_IMAGE',
});

const ACTION_TIMEOUT_MS = 15_000;
const HOUR_MS = 60 * 60 * 1000;
const DEFAULT_TIMEZONE = 'America/Fortaleza';

function actionId() {
  try { return `act-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`; }
  catch { return `act-${Math.random().toString(36).slice(2)}`; }
}

function trimText(value, maximum = 1_000) {
  return typeof value === 'string'
    ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, maximum)
    : '';
}

function normalize(value) {
  return trimText(value, 2_000).toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function timezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE; }
  catch { return DEFAULT_TIMEZONE; }
}

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full', timeStyle: 'short' }).format(date);
}

function result({ actionId: id, type, status, success, provider, entityId = null, errorCode = null, userMessage, ...extra }) {
  return {
    actionId: id,
    type,
    status,
    success: success === true,
    provider,
    entityId: entityId || null,
    errorCode: errorCode || null,
    userMessage: trimText(userMessage, 1_000),
    ...extra,
  };
}

function failureFrom(error, args) {
  const failure = normalizeAthenaApiFailure(error);
  return result({
    ...args,
    status: 'failed',
    success: false,
    errorCode: failure.code,
    userMessage: failure.userMessage,
    requestId: failure.requestId || null,
  });
}

function authenticatedConfig(token, extra = {}) {
  return {
    headers: { Authorization: `Bearer ${token}`, ...(extra.headers || {}) },
    withCredentials: true,
    timeout: ACTION_TIMEOUT_MS,
    ...extra,
  };
}

function dateWithTime({ base = new Date(), hour, minute }) {
  const date = new Date(base);
  date.setHours(hour, minute, 0, 0);
  return date;
}

function parseRelativeTime(text) {
  const match = text.match(/\b(?:daqui\s+a|em)\s+(\d{1,4})\s*(minuto(?:s)?|min|hora(?:s)?|h)\b/i);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isInteger(amount) || amount < 1 || amount > 720) return { invalid: true };
  const multiplier = /^h|hora/i.test(match[2]) ? HOUR_MS : 60_000;
  return { dueAt: new Date(Date.now() + amount * multiplier).toISOString() };
}

function parseClockTime(text) {
  const match = text.match(/\b(?:as|a|para)\s*(\d{1,2})(?:\s*(?:h|horas?)\s*(\d{1,2})?|:(\d{2}))?\b/i)
    || text.match(/\b(\d{1,2})\s*h\s*(\d{0,2})\b/i);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2] || match[3] || 0);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) return { invalid: true };
  const date = dateWithTime({ hour, minute });
  if (date.getTime() <= Date.now()) date.setDate(date.getDate() + 1);
  return { dueAt: date.toISOString(), hour, minute };
}

function stripActionTiming(raw) {
  return trimText(raw
    .replace(/\b(?:me\s+)?(?:lembre|lembrar|lembrete|acorde|desperte|despertador|alarme|coloca|coloque|cria|crie|programa|programe)\b/ig, '')
    .replace(/\b(?:daqui\s+a|em)\s+\d{1,4}\s*(?:minuto(?:s)?|min|hora(?:s)?|h)\b/ig, '')
    .replace(/\b(?:as|às|a|para)\s+\d{1,2}(?:(?:\s*h\s*\d{0,2})|(?::\d{2})|(?:\s+horas?))?\b/ig, '')
    .replace(/\b(?:hoje|amanha|amanhã)\b/ig, '')
    .replace(/^[,;:\-–—\s]+|[,;:\-–—\s]+$/g, ''), 180);
}

function parseReminderIntent(raw) {
  const text = normalize(raw);
  const hasAlarm = /\b(?:alarme|acorde|desperte|despertador)\b/.test(text);
  const hasReminder = /\b(?:lembre|lembrar|lembrete)\b/.test(text);
  if (!hasAlarm && !hasReminder) return null;
  const type = hasAlarm ? ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_ALARM : ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_REMINDER;
  const relative = parseRelativeTime(text);
  const clock = relative || parseClockTime(text);
  if (!clock || clock.invalid) {
    return { type, incomplete: true, message: hasAlarm ? 'Que horas você quer para o alarme?' : 'Que horas você quer para esse lembrete?' };
  }
  const subject = stripActionTiming(raw).replace(/^\b(?:de|para)\s+/i, '');
  const body = hasAlarm
    ? (subject ? `Hora de acordar: ${subject}` : 'Hora de acordar!')
    : (subject ? `Lembrete da Athena: ${subject}` : 'Lembrete da Athena');
  return { type, kind: hasAlarm ? 'alarm' : 'reminder', dueAt: clock.dueAt, body, subject, hour: clock.hour, minute: clock.minute };
}

function parseProjectIntent(raw) {
  const match = raw.match(/\b(?:cria|crie|novo|inicie|inicia)\s+(?:um\s+)?projeto(?:\s+(?:chamado|nomeado|com\s+(?:o\s+)?nome))?\s*[“"']?(.+?)[”"']?\s*$/i);
  if (!match) return null;
  const name = trimText(match[1], 120);
  return name
    ? { type: ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_PROJECT, name }
    : { type: ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_PROJECT, incomplete: true, message: 'Qual nome você quer dar ao projeto?' };
}

function routineTime(raw) {
  const contextual = /\b(?:as|às)\s*(\d{1,2})(?:\s*h(?:\s*(\d{1,2}))?|:(\d{2}))?\b/i.exec(raw);
  const compact = contextual || /\b(\d{1,2})\s*h(?:\s*(\d{1,2}))?\b/i.exec(raw);
  if (!compact) return null;
  const hour = Number(compact[1]);
  const minute = Number(compact[2] || compact[3] || 0);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) return { invalid: true };
  return {
    value: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    index: Number(compact.index) || 0,
    length: compact[0].length,
  };
}

function routineDuration(raw) {
  const match = /\b(?:por|durante)\s+(\d{1,3})\s*(minutos?|mins?|horas?|h)\b/i.exec(raw);
  if (!match) return 30;
  const amount = Number(match[1]);
  const minutes = /^(?:h|hora)/i.test(match[2]) ? amount * 60 : amount;
  return Number.isInteger(minutes) && minutes >= 5 && minutes <= 720 ? minutes : 30;
}

function routineDays(raw) {
  const text = normalize(raw);
  if (/\b(?:todos\s+os\s+dias|diariamente)\b/.test(text)) return [1, 2, 3, 4, 5, 6, 7];
  if (/\b(?:dias\s+uteis|dias\s+úteis|segunda\s+a\s+sexta)\b/.test(text)) return [1, 2, 3, 4, 5];
  const labels = [
    ['segunda', 1], ['terca', 2], ['quarta', 3], ['quinta', 4],
    ['sexta', 5], ['sabado', 6], ['domingo', 7],
  ];
  const days = labels.filter(([label]) => new RegExp(`\\b${label}(?:-feira)?\\b`).test(text)).map(([, day]) => day);
  return days.length ? [...new Set(days)].sort((left, right) => left - right) : null;
}

function cleanRoutineTitle(value) {
  return trimText(String(value || '')
    .replace(/^\s*(?:de|para|chamad[ao]|nomead[ao])\s+/i, '')
    .replace(/\s*(?:por|durante)\s+\d{1,3}\s*(?:minutos?|mins?|horas?|h)\b[\s\S]*$/i, '')
    .replace(/\s*\b(?:todos\s+os\s+dias|diariamente|dias\s+[úu]teis|segunda\s+a\s+sexta)\b[\s\S]*$/i, '')
    .replace(/\s+(?:para|as|às)\s*$/i, '')
    .replace(/^[,;:\-–—\s]+|[,;:\-–—\s]+$/g, ''), 160);
}

function routineTitle(raw, time) {
  if (!time || time.invalid) return '';
  const routineMatch = /\brotina\b/i.exec(raw);
  if (!routineMatch) return '';
  const afterRoutine = routineMatch.index + routineMatch[0].length;
  const beforeTime = raw.slice(afterRoutine, time.index);
  const afterTime = raw.slice(time.index + time.length);
  return cleanRoutineTitle(beforeTime) || cleanRoutineTitle(afterTime);
}

function parseRoutineIntent(raw) {
  const text = normalize(raw);
  const isCreate = /\b(?:cria|crie|adiciona|adicione|configura|configure)\b[\s\S]{0,48}\brotina\b/.test(text);
  const isUpdate = /\b(?:atualiza|atualize|altera|altere|muda|mude|edita|edite|reagenda|reagende)\b[\s\S]{0,48}\brotina\b/.test(text);
  if (!isCreate && !isUpdate) return null;
  const time = routineTime(raw);
  const title = routineTitle(raw, time);
  const type = isUpdate
    ? ATHENA_OPERATIONAL_ACTION_TYPES.UPDATE_ROUTINE
    : ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_ROUTINE;
  if (!time || time.invalid || !title) {
    return {
      type,
      incomplete: true,
      message: isUpdate
        ? 'Diga qual rotina devo atualizar e o novo horário, por exemplo: “mude a rotina de leitura para às 20h”.'
        : 'Diga o nome e o horário da rotina, por exemplo: “crie uma rotina de leitura às 20h”.',
    };
  }
  return {
    type,
    title,
    startTime: time.value,
    durationMinutes: routineDuration(raw),
    daysOfWeek: routineDays(raw),
  };
}

function previousConversationContent(raw, conversation = []) {
  const lastAssistant = [...conversation].reverse().find((message) => message?.role === 'assistant' && trimText(message.content));
  const lastUser = [...conversation].reverse().find((message) => message?.role === 'user'
    && trimText(message.content) && trimText(message.content) !== trimText(raw));
  return trimText(lastAssistant?.content || lastUser?.content || '', 10_000);
}

function captureTitle(content, fallback) {
  return trimText(String(content || '').split(/[.!?\n]/)[0], 180) || fallback;
}

function parseInboxIntent(raw, conversation = []) {
  const text = normalize(raw);
  const matchesCapture = /\b(?:anota|anote)\b/.test(text)
    || /\b(?:registre|registra|captura|capture)\s+(?:isso|isto|essa|esta|na\s+inbox)\b/.test(text);
  const matchesIdea = /\b(?:guarda|guarde|salva|salve)\s+(?:essa|esta|uma|a)?\s*ideia\b/.test(text);
  if (!matchesCapture && !matchesIdea) return null;
  const command = matchesIdea
    ? /\b(?:guarda|guarde|salva|salve)\s+(?:(?:essa|esta|uma|a)\s+)?ideia\b/i
    : /\b(?:anota|anote|registre|registra|captura|capture)\b/i;
  const match = command.exec(raw);
  const explicit = trimText(raw.slice((match?.index || 0) + (match?.[0].length || 0))
    .replace(/^\s*(?:isso|isto|essa|esta|a)?\s*[:\-–—,]?\s*/i, ''), 10_000);
  const content = explicit.length >= 3 ? explicit : previousConversationContent(raw, conversation);
  const isIdea = matchesIdea || /\bideia\b/.test(text);
  if (!content) {
    return {
      type: ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_TO_INBOX,
      incomplete: true,
      message: 'Qual texto ou ideia você quer que eu coloque na Inbox?',
    };
  }
  return {
    type: ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_TO_INBOX,
    title: captureTitle(content, isIdea ? 'Ideia capturada' : 'Nota capturada'),
    content,
    kind: isIdea ? 'idea' : 'note',
    suggestedType: isIdea ? 'idea' : 'note',
  };
}

function parseProjectDecisionIntent(raw, conversation = [], projectId = null) {
  if (!projectId) return null;
  const command = /\b(?:registre|registrar|registra|salve|salvar|salva|guarde|guardar)\b[\s\S]{0,56}\b(?:como\s+)?(?:uma\s+)?decis[aã]o(?:\s+do\s+projeto)?\b/i;
  const match = command.exec(raw);
  if (!match) return null;
  const explicit = trimText(raw.slice((match.index || 0) + match[0].length)
    .replace(/^\s*(?:de|que|:)\s*/i, ''), 3_000);
  const content = explicit.length >= 3 ? explicit : previousConversationContent(raw, conversation);
  if (!content) {
    return {
      type: ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_PROJECT_DECISION,
      incomplete: true,
      message: 'Qual decisão deste projeto você quer registrar?',
    };
  }
  return {
    type: ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_PROJECT_DECISION,
    title: captureTitle(content, 'Decisão do projeto'),
    content,
  };
}

function latestProposalConversationAction(conversation = []) {
  return [...conversation].reverse()
    .map((message) => message?.action_result)
    .find((action) => action?.type === ATHENA_OPERATIONAL_ACTION_TYPES.REORGANIZE_DAY
      && (action?.proposalId || action?.entityId)
      && ['needs_confirmation', 'proposed', 'confirmed_pending_application'].includes(action?.status));
}

function parseDayProposalConfirmation(raw, conversation = []) {
  const text = normalize(raw);
  if (!/\b(?:confirmo|confirma|confirmar|pode\s+(?:confirmar|aplicar)|aplica|aplique|pode\s+seguir|pode\s+prosseguir)\b/.test(text)) return null;
  const proposal = latestProposalConversationAction(conversation);
  if (!proposal) return null;
  return {
    type: ATHENA_OPERATIONAL_ACTION_TYPES.CONFIRM_DAY_PROPOSAL,
    proposalId: proposal.proposalId || proposal.entityId,
  };
}

function parseReorganizeDayIntent(raw) {
  const text = normalize(raw);
  if (!/\b(?:reorganiza|reorganize|organiza|organize|replaneja|replaneje)\b[\s\S]{0,42}\b(?:meu|minha|o)\s+(?:dia|tarde)\b/.test(text)) return null;
  return {
    type: ATHENA_OPERATIONAL_ACTION_TYPES.REORGANIZE_DAY,
    scope: /\btarde\b/.test(text) ? 'afternoon' : 'day',
  };
}

function inferSavedContent(raw, conversation = []) {
  const text = normalize(raw);
  if (!/\b(?:salva|salve|guarda|guarde)\b/.test(text)
    || !/(?:\b(?:isso|isto|essa|este|receita|ideia|link|texto|nota)\b|pra\s+mim|para\s+mim)/.test(text)) return null;
  const lastAssistant = [...conversation].reverse().find((message) => message?.role === 'assistant' && trimText(message.content));
  const lastUser = [...conversation].reverse().find((message) => message?.role === 'user' && trimText(message.content) && message.content !== raw);
  const explicit = trimText(raw.replace(/^.*?\b(?:salva|salve|guarda|guarde)\b(?:\s+(?:isso|isto|pra\s+mim|para\s+mim))?\s*/i, ''), 10_000);
  const content = explicit.length >= 12 ? explicit : trimText(lastAssistant?.content || lastUser?.content || '', 10_000);
  if (!content) return { type: ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_CONTENT, incomplete: true, message: 'Qual conteúdo você quer que eu salve? Envie ou indique o texto primeiro.' };
  const category = /receita|ingrediente|cozinha/.test(`${text} ${normalize(content)}`) ? 'recipes' : 'files';
  const title = category === 'recipes'
    ? trimText(content.split(/[.!?\n]/)[0], 180) || 'Receita salva'
    : trimText(content.split(/[.!?\n]/)[0], 180) || 'Conteúdo salvo';
  return { type: ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_CONTENT, title, content, category };
}

function inferTaskIntent(raw, conversation = []) {
  const direct = parseTaskChatRequest(raw);
  if (direct) return { type: ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_TASK, request: direct };
  const text = normalize(raw);
  if (!/\b(?:transforma|transforme|converte|converta)\b[\s\S]{0,80}\b(?:em\s+)?(?:uma\s+)?tarefa\b/.test(text)) return null;
  const source = [...conversation].reverse().find((message) => message?.role === 'assistant' && trimText(message.content))
    || [...conversation].reverse().find((message) => message?.role === 'user' && trimText(message.content) && message.content !== raw);
  const title = trimText(source?.content || '', 160);
  return title
    ? { type: ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_TASK, request: { title, due_at: null, priority: 'normal' } }
    : { type: ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_TASK, request: { incomplete: true, message: 'Qual conteúdo você quer transformar em tarefa?' } };
}

function latestCompletedConversationAction(conversation, type) {
  return [...conversation].reverse()
    .map((message) => message?.action_result)
    .find((action) => action?.type === type && action?.success === true && action?.entityId);
}

function inferTaskProjectAssignment(raw, conversation = []) {
  const text = normalize(raw);
  if (!/\b(?:adicione|adiciona|adicionar|coloque|coloca|incluir|inclua)\b[\s\S]{0,80}\b(?:essa|esta|a)?\s*tarefa\b[\s\S]{0,80}\b(?:nesse|neste|no|na)\s+projeto\b/.test(text)) return null;
  const task = latestCompletedConversationAction(conversation, ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_TASK);
  const project = latestCompletedConversationAction(conversation, ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_PROJECT);
  if (!task?.entityId || !project?.entityId) {
    return {
      type: ATHENA_OPERATIONAL_ACTION_TYPES.ASSIGN_TASK_TO_PROJECT,
      incomplete: true,
      message: 'Para fazer esse vínculo com segurança, preciso que a tarefa e o projeto tenham sido criados nesta conversa ou que você me diga quais são.',
    };
  }
  return {
    type: ATHENA_OPERATIONAL_ACTION_TYPES.ASSIGN_TASK_TO_PROJECT,
    taskId: task.entityId,
    projectId: project.entityId,
    taskTitle: trimText(task?.responsePresentation?.title || '', 160) || 'a tarefa',
    projectName: trimText(project?.projectName || '', 120) || 'esse projeto',
  };
}

function parseCalendarUpdateIntent(raw) {
  const text = normalize(raw);
  if (!/\b(?:muda|mude|altera|alterar|reagenda|reagende|troca|troque)\b/.test(text)
    || !/\b(?:reuniao|evento|compromisso|agenda)\b/.test(text)) return null;
  const clock = parseClockTime(text);
  if (!clock || clock.invalid) return { type: ATHENA_OPERATIONAL_ACTION_TYPES.UPDATE_CALENDAR_EVENT, incomplete: true, message: 'Para qual horário você quer mudar esse compromisso?' };
  const target = new Date(clock.dueAt);
  if (/\bamanha\b/.test(text)) {
    // parseClockTime already moves a past clock to tomorrow. If "amanhã" was
    // explicit, reset it from today so a future clock does not stay on today.
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(clock.hour, clock.minute, 0, 0);
    target.setTime(tomorrow.getTime());
  }
  const hintMatch = raw.match(/(?:minha\s+)?(reuni[aã]o|evento|compromisso)(?:\s+de\s+[^,]+?)?\s+(?:para|pras?|às?|as)\s+/i);
  return {
    type: ATHENA_OPERATIONAL_ACTION_TYPES.UPDATE_CALENDAR_EVENT,
    titleHint: trimText(hintMatch?.[1] || 'reunião', 80),
    startAt: target.toISOString(),
  };
}

function parseCameraIntent(raw) {
  return /\b(?:abre|abra|abrir|tira|tirar|capture|capturar)\s+(?:a\s+)?(?:camera|câmera|foto)\b/i.test(raw)
    ? { type: ATHENA_OPERATIONAL_ACTION_TYPES.TAKE_PHOTO }
    : null;
}

function parseImageIntent(raw) {
  return /\b(?:gera|gere|cria|crie)\s+(?:uma\s+)?(?:imagem|foto|ilustra[çc][aã]o|arte|capa)\b/i.test(raw)
    ? { type: ATHENA_OPERATIONAL_ACTION_TYPES.GENERATE_IMAGE }
    : null;
}

function imagePromptFromMessage(raw) {
  const match = String(raw || '').match(/\b(?:gera|gere|cria|crie)\s+(?:uma\s+)?(?:imagem|foto|ilustra[çc][aã]o|arte|capa)(?:\s+(?:de|do|da|sobre|para))?\s*([\s\S]*)$/i);
  const prompt = trimText(match?.[1] || '', 1_000).replace(/^[,:;\-–—\s]+/, '');
  return prompt || null;
}

const EXTERNAL_SITES = Object.freeze({
  google: 'https://www.google.com',
  youtube: 'https://www.youtube.com',
  gmail: 'https://mail.google.com',
  canva: 'https://www.canva.com',
  figma: 'https://www.figma.com',
  notion: 'https://www.notion.so',
  whatsapp: 'https://web.whatsapp.com',
  instagram: 'https://www.instagram.com',
});

function parseExternalIntent(raw) {
  const text = normalize(raw);
  const match = text.match(/^\s*(?:athena[,:]?\s*)?(?:abre|abra|abrir)\s+(?:o\s+|a\s+)?([a-z]+)/i);
  if (!match || !EXTERNAL_SITES[match[1]]) return null;
  return { type: ATHENA_OPERATIONAL_ACTION_TYPES.OPEN_EXTERNAL, name: match[1], url: EXTERNAL_SITES[match[1]] };
}

/** Returns null only when the message is normal chat rather than an action. */
export function detectAthenaOperationalIntent(message, { conversation = [], projectId = null } = {}) {
  const raw = trimText(message, 10_000);
  if (!raw) return null;
  return parseDayProposalConfirmation(raw, conversation)
    || parseReorganizeDayIntent(raw)
    || parseRoutineIntent(raw)
    || parseProjectDecisionIntent(raw, conversation, projectId)
    || parseInboxIntent(raw, conversation)
    || parseReminderIntent(raw)
    || parseCalendarUpdateIntent(raw)
    || (() => {
      const request = parseCalendarChatRequest(raw);
      return request ? { type: ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_CALENDAR_EVENT, request } : null;
    })()
    || inferTaskProjectAssignment(raw, conversation)
    || inferTaskIntent(raw, conversation)
    || parseProjectIntent(raw)
    || inferSavedContent(raw, conversation)
    || parseCameraIntent(raw)
    || parseImageIntent(raw)
    || parseExternalIntent(raw);
}

async function ensureServerCalendar({ apiBase, token, projectId }) {
  const { data } = await axios.get(`${apiBase}/calendars`, authenticatedConfig(token));
  let calendar = (data?.calendars || []).find((item) => item?.is_default) || data?.calendars?.[0];
  if (calendar?.id) return calendar;
  const created = await axios.post(`${apiBase}/calendars`, {
    name: 'Pessoal', color: '#3B82F6', is_default: true, project_id: projectId || null,
  }, authenticatedConfig(token));
  calendar = created?.data?.calendar;
  if (!calendar?.id) throw new Error('CALENDAR_SERVER_REFERENCE_MISSING');
  return calendar;
}

async function cancelServerReminder(apiBase, token, reminderId) {
  if (!reminderId) return false;
  try {
    await axios.delete(`${apiBase}/reminders/${encodeURIComponent(reminderId)}`, authenticatedConfig(token));
    return true;
  } catch {
    return false;
  }
}

async function deleteServerEvent(apiBase, token, eventId) {
  if (!eventId) return false;
  try {
    await axios.delete(`${apiBase}/events/${encodeURIComponent(eventId)}`, authenticatedConfig(token));
    return true;
  } catch {
    return false;
  }
}

function calendarNativeFailure(action, reason) {
  return result({
    actionId: action.id,
    type: action.type,
    status: 'failed',
    success: false,
    provider: 'android_calendar',
    errorCode: reason,
    userMessage: reason === 'CALENDAR_PERMISSION_DENIED'
      ? 'Não criei o evento porque o Android não liberou acesso ao calendário.'
      : 'Não consegui confirmar esse evento no calendário do aparelho. Nada foi confirmado como agendado.',
  });
}

async function executeReminder(action, context) {
  const { apiBase, token, intent } = context;
  if (intent.incomplete) return result({ actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'none', errorCode: 'TIME_REQUIRED', userMessage: intent.message });
  if (intent.kind === 'alarm' && !IS_NATIVE_APP) {
    return result({ actionId: action.id, type: action.type, status: 'unavailable', success: false, provider: 'none', errorCode: 'ALARM_REQUIRES_ANDROID', userMessage: 'O despertador precisa ser criado no aplicativo Android para eu confirmar o alarme do aparelho.' });
  }
  try {
    const { data } = await axios.post(`${apiBase}/reminders`, {
      body: intent.body,
      due_at: intent.dueAt,
      kind: intent.kind,
      timezone: timezone(),
    }, authenticatedConfig(token));
    const reminder = data?.reminder;
    if (!reminder?.id) throw new Error('REMINDER_REFERENCE_MISSING');

    if (intent.kind === 'alarm') {
      const scheduled = await scheduleAthenaAlarmForReminder(reminder, { route: '/' });
      const registry = scheduled?.scheduled === true ? await getAthenaNativeAlarms() : null;
      const expectedTrigger = Date.parse(reminder.due_at || intent.dueAt);
      const verified = registry?.alarms?.some((alarm) => alarm.id === reminder.id
        && Math.abs(Number(alarm.triggerAt) - expectedTrigger) < 2_000);
      if (!scheduled?.scheduled || !verified) {
        if (scheduled?.alarmId) await cancelAthenaNativeAlarm(scheduled.alarmId).catch(() => {});
        const rolledBack = await cancelServerReminder(apiBase, token, reminder.id);
        return result({
          actionId: action.id,
          type: action.type,
          status: 'failed',
          success: false,
          provider: 'android_alarm_manager',
          entityId: reminder.id,
          errorCode: scheduled?.reason || registry?.status || 'ALARM_NOT_VERIFIED',
          userMessage: rolledBack
            ? 'Não consegui confirmar o alarme no Android, então não o deixei agendado.'
            : 'Não consegui confirmar o alarme no Android. A Athena não vai tratá-lo como criado.',
        });
      }
    }

    const when = formatWhen(intent.dueAt);
    const isAlarm = intent.kind === 'alarm';
    return result({
      actionId: action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: isAlarm ? 'android_alarm_manager' : 'athena_reminders',
      entityId: reminder.id,
      userMessage: isAlarm
        ? `Pronto: confirmei o alarme no Android para ${when}.`
        : `Pronto: o lembrete foi salvo para ${when}.`,
      responsePresentation: reminderPresentation({
        title: isAlarm ? 'Despertador da Athena' : 'Lembrete da Athena',
        summary: isAlarm ? `Alarme confirmado para ${when}.` : `Aviso programado para ${when}.`,
        when: intent.dueAt,
        items: [intent.body],
      }),
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: intent.kind === 'alarm' ? 'android_alarm_manager' : 'athena_reminders' });
  }
}

async function chooseNativeCalendar() {
  const permission = await requestNativeCalendarAccess({ write: true });
  if (permission?.calendarWrite !== true) return { error: 'CALENDAR_PERMISSION_DENIED' };
  const calendarData = await getNativeCalendars();
  const calendar = (calendarData.calendars || []).find((item) => item?.canWrite === true);
  if (!calendar?.deviceCalendarId) return { error: 'CALENDAR_WRITABLE_CALENDAR_UNAVAILABLE' };
  return { calendar };
}

async function executeCalendarCreate(action, context) {
  const { apiBase, token, intent, projectId } = context;
  const request = intent.request;
  if (request?.incomplete) return result({ actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'none', errorCode: 'CALENDAR_DETAILS_REQUIRED', userMessage: request.message });
  if (!request?.title || !request?.start_at) return calendarNativeFailure(action, 'CALENDAR_DETAILS_REQUIRED');

  let deviceCalendar = null;
  if (IS_NATIVE_APP) {
    const choice = await chooseNativeCalendar();
    if (choice.error) return calendarNativeFailure(action, choice.error);
    deviceCalendar = choice.calendar;
  }

  const startAt = new Date(request.start_at);
  const endAt = new Date(startAt.getTime() + HOUR_MS);
  let serverEvent = null;
  try {
    const calendar = await ensureServerCalendar({ apiBase, token, projectId });
    const { data } = await axios.post(`${apiBase}/events`, {
      calendar_id: calendar.id,
      project_id: projectId || null,
      title: request.title,
      start_at: startAt.toISOString(),
      end_at: endAt.toISOString(),
      timezone: timezone(),
      reminder_minutes: defaultReminderMinutesFor(startAt.toISOString()),
      idempotency_key: `${action.id}:calendar`,
    }, authenticatedConfig(token));
    serverEvent = data?.event;
    if (!serverEvent?.id) throw new Error('CALENDAR_EVENT_REFERENCE_MISSING');
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_calendar' });
  }

  if (!IS_NATIVE_APP) {
    const when = formatWhen(startAt);
    return result({
      actionId: action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: 'athena_calendar',
      entityId: serverEvent.id,
      userMessage: `Pronto: salvei “${request.title}” para ${when} no calendário da Athena.`,
      responsePresentation: reminderPresentation({ title: request.title, summary: `Compromisso para ${when}.`, when: startAt.toISOString(), items: ['Sincronização com o calendário do aparelho requer o app Android.'] }),
    });
  }

  let native;
  try {
    native = await createNativeCalendarEvent({
      title: request.title,
      deviceCalendarId: String(deviceCalendar.deviceCalendarId),
      startMs: startAt.getTime(),
      endMs: endAt.getTime(),
      timezone: timezone(),
    });
  } catch {
    await deleteServerEvent(apiBase, token, serverEvent.id);
    return calendarNativeFailure(action, 'CALENDAR_NATIVE_CREATE_FAILED');
  }
  if (native?.created !== true || !native?.deviceEventId) {
    await deleteServerEvent(apiBase, token, serverEvent.id);
    return calendarNativeFailure(action, native?.status || 'CALENDAR_NATIVE_CREATE_FAILED');
  }

  // Insertion alone is not proof enough: query the Android calendar registry
  // for the returned provider event ID before we can say it was scheduled.
  let verified = false;
  try {
    const registry = await listNativeCalendarEvents({
      deviceCalendarId: String(native.deviceCalendarId),
      startMs: startAt.getTime() - 60_000,
      endMs: endAt.getTime() + 60_000,
    });
    verified = registry?.events?.some((event) => String(event?.deviceEventId) === String(native.deviceEventId)
      && String(event?.deviceCalendarId) === String(native.deviceCalendarId));
  } catch {
    verified = false;
  }
  if (!verified) {
    await deleteNativeCalendarEvent({ deviceCalendarId: String(native.deviceCalendarId), deviceEventId: String(native.deviceEventId), confirmed: true, confirmationToken: action.id }).catch(() => {});
    await deleteServerEvent(apiBase, token, serverEvent.id);
    return calendarNativeFailure(action, 'CALENDAR_NOT_VERIFIED');
  }

  try {
    await axios.post(`${apiBase}/events/${encodeURIComponent(serverEvent.id)}/device-links`, {
      device_calendar_id: String(native.deviceCalendarId),
      device_event_id: String(native.deviceEventId),
      sync_state: 'linked',
      last_synced_at: new Date().toISOString(),
    }, authenticatedConfig(token));
  } catch (error) {
    await deleteNativeCalendarEvent({ deviceCalendarId: String(native.deviceCalendarId), deviceEventId: String(native.deviceEventId), confirmed: true, confirmationToken: action.id }).catch(() => {});
    await deleteServerEvent(apiBase, token, serverEvent.id);
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'android_calendar' });
  }

  const when = formatWhen(startAt);
  return result({
    actionId: action.id,
    type: action.type,
    status: 'completed',
    success: true,
    provider: 'android_calendar',
    entityId: serverEvent.id,
    deviceEventId: String(native.deviceEventId),
    userMessage: `Pronto: confirmei “${request.title}” para ${when} no calendário do aparelho.`,
    responsePresentation: reminderPresentation({ title: request.title, summary: `Evento confirmado no dispositivo para ${when}.`, when: startAt.toISOString(), items: ['Evento salvo na Athena e vinculado ao calendário Android.'] }),
  });
}

async function executeCalendarUpdate(action, context) {
  const { apiBase, token, intent } = context;
  if (intent.incomplete) return result({ actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'none', errorCode: 'TIME_REQUIRED', userMessage: intent.message });
  try {
    const dayStart = new Date(intent.startAt); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + 24 * HOUR_MS);
    const { data } = await axios.get(`${apiBase}/events`, authenticatedConfig(token, {
      params: { from: dayStart.toISOString(), to: dayEnd.toISOString() },
    }));
    const hint = normalize(intent.titleHint);
    const event = (data?.events || []).find((item) => normalize(item?.title).includes(hint));
    if (!event?.id) return result({ actionId: action.id, type: action.type, status: 'failed', success: false, provider: 'athena_calendar', errorCode: 'CALENDAR_EVENT_NOT_FOUND', userMessage: 'Não encontrei esse compromisso para alterar, então não criei um evento novo.' });

    const nextStart = new Date(intent.startAt);
    const previousStart = new Date(event.start_at);
    const previousEnd = event.end_at ? new Date(event.end_at) : new Date(previousStart.getTime() + HOUR_MS);
    const duration = Math.max(15 * 60_000, previousEnd.getTime() - previousStart.getTime());
    if (IS_NATIVE_APP) {
      const { data: links } = await axios.get(`${apiBase}/events/${encodeURIComponent(event.id)}/device-links`, authenticatedConfig(token));
      const link = links?.links?.[0];
      if (!link?.device_calendar_id || !link?.device_event_id) return result({ actionId: action.id, type: action.type, status: 'failed', success: false, provider: 'android_calendar', entityId: event.id, errorCode: 'CALENDAR_DEVICE_LINK_MISSING', userMessage: 'Esse compromisso não está vinculado ao calendário do aparelho, então não o alterei para evitar duplicidade.' });
      const native = await updateNativeCalendarEvent({
        deviceCalendarId: String(link.device_calendar_id),
        deviceEventId: String(link.device_event_id),
        startMs: nextStart.getTime(),
        endMs: nextStart.getTime() + duration,
        timezone: timezone(),
      });
      if (native?.updated !== true) return calendarNativeFailure(action, native?.status || 'CALENDAR_NATIVE_UPDATE_FAILED');
      let nativeVerified = false;
      try {
        const registry = await listNativeCalendarEvents({
          deviceCalendarId: String(link.device_calendar_id),
          startMs: nextStart.getTime() - 60_000,
          endMs: nextStart.getTime() + duration + 60_000,
        });
        nativeVerified = registry?.events?.some((candidate) => String(candidate?.deviceEventId) === String(link.device_event_id)
          && Math.abs(Number(candidate?.startMs) - nextStart.getTime()) < 2_000);
      } catch {
        nativeVerified = false;
      }
      if (!nativeVerified) {
        await updateNativeCalendarEvent({ deviceCalendarId: String(link.device_calendar_id), deviceEventId: String(link.device_event_id), startMs: previousStart.getTime(), endMs: previousEnd.getTime(), timezone: timezone() }).catch(() => {});
        return calendarNativeFailure(action, 'CALENDAR_UPDATE_NOT_VERIFIED');
      }
      try {
        const update = await axios.patch(`${apiBase}/events/${encodeURIComponent(event.id)}`, { start_at: nextStart.toISOString() }, authenticatedConfig(token));
        if (update?.data?.ok !== true) throw new Error('CALENDAR_SERVER_UPDATE_NOT_CONFIRMED');
      } catch (error) {
        await updateNativeCalendarEvent({ deviceCalendarId: String(link.device_calendar_id), deviceEventId: String(link.device_event_id), startMs: previousStart.getTime(), endMs: previousEnd.getTime(), timezone: timezone() }).catch(() => {});
        return failureFrom(error, { actionId: action.id, type: action.type, provider: 'android_calendar', entityId: event.id });
      }
      return result({ actionId: action.id, type: action.type, status: 'completed', success: true, provider: 'android_calendar', entityId: event.id, userMessage: `Pronto: confirmei a alteração de “${event.title}” para ${formatWhen(nextStart)} no calendário do aparelho.` });
    }
    const update = await axios.patch(`${apiBase}/events/${encodeURIComponent(event.id)}`, { start_at: nextStart.toISOString() }, authenticatedConfig(token));
    if (update?.data?.ok !== true) throw new Error('CALENDAR_SERVER_UPDATE_NOT_CONFIRMED');
    return result({ actionId: action.id, type: action.type, status: 'completed', success: true, provider: 'athena_calendar', entityId: event.id, userMessage: `Pronto: alterei “${event.title}” para ${formatWhen(nextStart)} no calendário da Athena.` });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: IS_NATIVE_APP ? 'android_calendar' : 'athena_calendar' });
  }
}

async function executeTask(action, context) {
  const { apiBase, token, intent, projectId } = context;
  const request = intent.request;
  if (request?.incomplete) return result({ actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'none', errorCode: 'TASK_DETAILS_REQUIRED', userMessage: request.message });
  try {
    const { data } = await axios.post(`${apiBase}/tasks`, {
      title: request.title,
      due_at: request.due_at || null,
      priority: request.priority || 'normal',
      project_id: projectId || null,
      timezone: timezone(),
      reminder_minutes: request.due_at ? reminderMinutesForTask(request.due_at) : [],
      idempotency_key: `${action.id}:task`,
    }, authenticatedConfig(token));
    const task = data?.task;
    if (!task?.id) throw new Error('TASK_REFERENCE_MISSING');
    const when = request.due_at ? ` para ${formatWhen(request.due_at)}` : '';
    return result({
      actionId: action.id, type: action.type, status: 'completed', success: true, provider: 'athena_tasks', entityId: task.id,
      userMessage: `Pronto: registrei a tarefa “${request.title}”${when}.`,
      responsePresentation: taskPresentation({ title: request.title, summary: request.due_at ? `Prazo definido${when}.` : 'Tarefa registrada sem prazo.', when: request.due_at || undefined, priority: request.priority === 'high' ? 'Prioridade alta' : 'Prioridade normal' }),
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_tasks' });
  }
}

async function executeTaskProjectAssignment(action, context) {
  const { apiBase, token, intent } = context;
  if (intent.incomplete) return result({ actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'none', errorCode: 'TASK_PROJECT_REFERENCE_REQUIRED', userMessage: intent.message });
  try {
    const { data } = await axios.patch(`${apiBase}/tasks/${encodeURIComponent(intent.taskId)}`, {
      project_id: intent.projectId,
    }, authenticatedConfig(token));
    if (data?.ok !== true) throw new Error('TASK_PROJECT_UPDATE_NOT_CONFIRMED');
    const verification = await axios.get(`${apiBase}/tasks`, authenticatedConfig(token, {
      params: { project_id: intent.projectId },
    }));
    const assigned = (verification?.data?.tasks || []).find((task) => String(task?.id) === String(intent.taskId)
      && String(task?.project_id) === String(intent.projectId));
    if (!assigned) {
      return result({ actionId: action.id, type: action.type, status: 'failed', success: false, provider: 'athena_projects', entityId: intent.taskId, errorCode: 'TASK_PROJECT_NOT_VERIFIED', userMessage: 'Não consegui confirmar o vínculo da tarefa com o projeto, então não vou tratá-lo como concluído.' });
    }
    return result({
      actionId: action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: 'athena_projects',
      entityId: intent.taskId,
      projectId: intent.projectId,
      userMessage: `Pronto: vinculei “${assigned.title || intent.taskTitle}” a ${intent.projectName}.`,
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_projects', entityId: intent.taskId });
  }
}

async function executeProject(action, context) {
  const { apiBase, token, intent } = context;
  if (intent.incomplete) return result({ actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'none', errorCode: 'PROJECT_NAME_REQUIRED', userMessage: intent.message });
  try {
    const { data } = await axios.post(`${apiBase}/projects`, { name: intent.name }, authenticatedConfig(token));
    const project = data?.project;
    const projectId = project?.project_id || project?.id;
    if (!projectId) throw new Error('PROJECT_REFERENCE_MISSING');
    return result({ actionId: action.id, type: action.type, status: 'completed', success: true, provider: 'athena_projects', entityId: projectId, projectName: project.name || intent.name, userMessage: `Pronto: criei o projeto “${project.name || intent.name}”.` });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_projects' });
  }
}

async function executeCreateRoutine(action, context) {
  const { apiBase, token, intent, projectId } = context;
  if (intent.incomplete) {
    return result({
      actionId: action.id,
      type: action.type,
      status: 'needs_input',
      success: false,
      provider: 'none',
      errorCode: 'ROUTINE_DETAILS_REQUIRED',
      userMessage: intent.message,
    });
  }
  try {
    const { data } = await axios.post(`${apiBase}/life/routines`, {
      title: intent.title,
      start_time: intent.startTime,
      duration_minutes: intent.durationMinutes,
      days_of_week: intent.daysOfWeek || undefined,
      project_id: projectId || null,
      idempotency_key: `${action.id}:routine`,
    }, authenticatedConfig(token));
    const routine = data?.routine;
    const verified = routine?.id
      && normalize(routine.title) === normalize(intent.title)
      && routine.start_time === intent.startTime;
    if (!verified) throw new Error('ROUTINE_REFERENCE_MISSING');
    return result({
      actionId: action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: 'athena_life_os',
      entityId: routine.id,
      routine,
      userMessage: data?.duplicate
        ? `A rotina “${routine.title}” já estava salva para ${routine.start_time}.`
        : `Pronto: criei a rotina “${routine.title}” às ${routine.start_time}.`,
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_life_os' });
  }
}

async function executeUpdateRoutine(action, context) {
  const { apiBase, token, intent } = context;
  if (intent.incomplete) {
    return result({
      actionId: action.id,
      type: action.type,
      status: 'needs_input',
      success: false,
      provider: 'none',
      errorCode: 'ROUTINE_DETAILS_REQUIRED',
      userMessage: intent.message,
    });
  }
  try {
    const listed = await axios.get(`${apiBase}/life/routines`, authenticatedConfig(token, {
      params: { status: 'all', limit: 100 },
    }));
    const candidates = (listed?.data?.routines || []).filter((routine) => normalize(routine?.title) === normalize(intent.title));
    if (!candidates.length) {
      return result({
        actionId: action.id,
        type: action.type,
        status: 'failed',
        success: false,
        provider: 'athena_life_os',
        errorCode: 'ROUTINE_NOT_FOUND',
        userMessage: `Não encontrei uma rotina chamada “${intent.title}”, então não alterei nenhuma rotina.`,
      });
    }
    if (candidates.length > 1) {
      return result({
        actionId: action.id,
        type: action.type,
        status: 'needs_input',
        success: false,
        provider: 'athena_life_os',
        errorCode: 'ROUTINE_AMBIGUOUS',
        userMessage: `Encontrei mais de uma rotina chamada “${intent.title}”. Diga qual delas devo atualizar.`,
      });
    }
    const target = candidates[0];
    const { data } = await axios.patch(`${apiBase}/life/routines/${encodeURIComponent(target.id)}`, {
      start_time: intent.startTime,
      duration_minutes: intent.durationMinutes,
      days_of_week: intent.daysOfWeek || target.days_of_week,
    }, authenticatedConfig(token));
    const routine = data?.routine;
    const verified = routine?.id === target.id
      && routine.start_time === intent.startTime
      && normalize(routine.title) === normalize(target.title);
    if (!verified) throw new Error('ROUTINE_UPDATE_NOT_VERIFIED');
    return result({
      actionId: action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: 'athena_life_os',
      entityId: routine.id,
      routine,
      userMessage: `Pronto: atualizei a rotina “${routine.title}” para ${routine.start_time}.`,
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_life_os' });
  }
}

function hourInTimezone(value, timezoneName) {
  const milliseconds = Date.parse(value || '');
  if (!Number.isFinite(milliseconds)) return null;
  try {
    const rendered = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezoneName || timezone(),
      hour: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(milliseconds));
    const hour = Number(rendered);
    return Number.isInteger(hour) ? hour : null;
  } catch {
    return new Date(milliseconds).getHours();
  }
}

function dayProposalDraft(today, scope) {
  const isAfternoon = scope === 'afternoon';
  const timezoneName = today?.timezone || timezone();
  const freeBlocks = (Array.isArray(today?.free_blocks) ? today.free_blocks : [])
    .filter((block) => Number(block?.duration_minutes) >= 15
      && (!isAfternoon || (hourInTimezone(block?.start_at, timezoneName) || 0) >= 12))
    .map((block) => ({
      start: Date.parse(block.start_at || ''),
      end: Date.parse(block.end_at || ''),
      duration: Number(block.duration_minutes) || 0,
    }))
    .filter((block) => Number.isFinite(block.start) && Number.isFinite(block.end) && block.end > block.start);
  const candidateItems = [
    ...(Array.isArray(today?.to_place) ? today.to_place : []),
    ...(Array.isArray(today?.unscheduled_tasks) ? today.unscheduled_tasks : []),
  ];
  const taskIds = new Set();
  const tasks = candidateItems.filter((item) => {
    const entityId = String(item?.entity_id || '');
    const isTask = normalize(item?.type) === 'task';
    if (!isTask || !entityId || taskIds.has(entityId)) return false;
    taskIds.add(entityId);
    return true;
  }).sort((left, right) => {
    const priorityWeight = { urgent: 0, high: 1, normal: 2, low: 3 };
    return (priorityWeight[left?.priority] ?? 2) - (priorityWeight[right?.priority] ?? 2);
  });
  const changes = [];
  let blockIndex = 0;
  let cursor = freeBlocks[0]?.start || null;
  for (const task of tasks.slice(0, 3)) {
    while (blockIndex < freeBlocks.length && (!cursor || cursor >= freeBlocks[blockIndex].end)) {
      blockIndex += 1;
      cursor = freeBlocks[blockIndex]?.start || null;
    }
    if (!cursor || blockIndex >= freeBlocks.length) break;
    const block = freeBlocks[blockIndex];
    const availableMinutes = Math.floor((block.end - cursor) / 60_000);
    if (availableMinutes < 15) continue;
    const durationMinutes = Math.min(60, availableMinutes);
    changes.push({
      type: 'schedule_task',
      entity_id: String(task.entity_id),
      title: trimText(task.title, 180),
      to: new Date(cursor).toISOString(),
      duration_minutes: durationMinutes,
      reason: 'Tarefa sem horário alocada em bloco livre, sem deslocar compromissos fixos.',
    });
    cursor += durationMinutes * 60_000;
  }
  const conflicts = (Array.isArray(today?.conflicts) ? today.conflicts : []).slice(0, 8).map((conflict) => ({
    code: conflict?.type === 'overlap' ? 'overlap' : 'other',
    summary: trimText(conflict?.type === 'overlap'
      ? 'Há um conflito de horários que foi preservado para revisão.'
      : 'Há um ponto do dia que precisa de revisão.', 600),
  }));
  if (tasks.length > changes.length) {
    conflicts.push({
      code: 'unscheduled_task',
      summary: 'Nem todas as tarefas sem horário cabem em blocos livres seguros.',
    });
  }
  const scopeLabel = isAfternoon ? 'sua tarde' : 'seu dia';
  const reasoning = changes.length
    ? `Usei ${changes.length} bloco(s) livre(s) para propor espaço para tarefas sem horário, preservando eventos e rotinas já marcados em ${scopeLabel}.`
    : `Não encontrei uma alocação segura para tarefas sem horário em ${scopeLabel}; compromissos e rotinas atuais foram preservados.`;
  return { changes, conflicts, reasoning };
}

async function executeReorganizeDay(action, context) {
  const { apiBase, token, intent, projectId } = context;
  try {
    const todayResponse = await axios.get(`${apiBase}/life/today`, authenticatedConfig(token, {
      params: { timezone: timezone() },
    }));
    const today = todayResponse?.data;
    if (!today?.date || !Array.isArray(today?.timeline) || !Array.isArray(today?.free_blocks)) {
      throw new Error('LIFE_OS_TODAY_REFERENCE_MISSING');
    }
    const draft = dayProposalDraft(today, intent.scope);
    const { data } = await axios.post(`${apiBase}/life/plan-proposals`, {
      date: today.date,
      timezone: today.timezone || timezone(),
      scope: 'day',
      project_id: projectId || null,
      changes: draft.changes,
      conflicts: draft.conflicts,
      reasoning_summary: draft.reasoning,
      requires_confirmation: true,
      idempotency_key: `${action.id}:day-proposal`,
    }, authenticatedConfig(token));
    const proposal = data?.proposal;
    const verified = proposal?.id && proposal.status === 'pending' && proposal.requires_confirmation === true;
    if (!verified) throw new Error('PLAN_PROPOSAL_NOT_VERIFIED');
    const scopeLabel = intent.scope === 'afternoon' ? 'sua tarde' : 'seu dia';
    const changeText = draft.changes.length
      ? `${draft.changes.length} sugestão(ões) de horário`
      : 'nenhuma alteração automática segura';
    return result({
      actionId: action.id,
      type: action.type,
      status: 'needs_confirmation',
      success: false,
      provider: 'athena_life_os',
      entityId: proposal.id,
      proposalId: proposal.id,
      proposal,
      userMessage: `Preparei uma proposta para ${scopeLabel} com ${changeText}. Ela foi salva para sua revisão; não alterei agenda, tarefas ou rotinas. Confirme se quiser seguir.`,
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_life_os' });
  }
}

async function executeConfirmDayProposal(action, context) {
  const { apiBase, token, intent } = context;
  if (!intent.proposalId) {
    return result({
      actionId: action.id,
      type: action.type,
      status: 'needs_input',
      success: false,
      provider: 'none',
      errorCode: 'PLAN_PROPOSAL_REFERENCE_REQUIRED',
      userMessage: 'Não encontrei uma proposta pendente para confirmar nesta conversa.',
    });
  }
  try {
    const { data } = await axios.patch(`${apiBase}/life/plan-proposals/${encodeURIComponent(intent.proposalId)}`, {
      status: 'confirmed',
    }, authenticatedConfig(token));
    const proposal = data?.proposal;
    if (!proposal?.id || proposal.id !== intent.proposalId || proposal.status !== 'confirmed') {
      throw new Error('PLAN_PROPOSAL_CONFIRMATION_NOT_VERIFIED');
    }
    if (data?.applied === true) {
      return result({
        actionId: action.id,
        type: action.type,
        status: 'completed',
        success: true,
        provider: 'athena_life_os',
        entityId: proposal.id,
        proposalId: proposal.id,
        proposal,
        userMessage: 'Pronto: confirmei e apliquei a proposta do dia.',
      });
    }
    return result({
      actionId: action.id,
      type: action.type,
      status: 'confirmed_pending_application',
      success: false,
      provider: 'athena_life_os',
      entityId: proposal.id,
      proposalId: proposal.id,
      proposal,
      userMessage: 'Registrei sua confirmação. Esta versão ainda não aplicou mudanças automaticamente, então agenda, tarefas e rotinas permanecem inalteradas.',
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_life_os', entityId: intent.proposalId });
  }
}

async function executeSaveToInbox(action, context) {
  const { apiBase, token, intent, projectId, sessionId } = context;
  if (intent.incomplete) {
    return result({
      actionId: action.id,
      type: action.type,
      status: 'needs_input',
      success: false,
      provider: 'none',
      errorCode: 'INBOX_CONTENT_REQUIRED',
      userMessage: intent.message,
    });
  }
  try {
    const { data } = await axios.post(`${apiBase}/life/inbox`, {
      title: intent.title,
      content: intent.content,
      kind: intent.kind,
      suggested_type: intent.suggestedType,
      source: 'chat',
      source_reference: sessionId ? `session:${sessionId}` : undefined,
      project_id: projectId || null,
      idempotency_key: `${action.id}:inbox`,
    }, authenticatedConfig(token));
    const item = data?.item;
    if (!item?.id || item.status !== 'pending') throw new Error('INBOX_ITEM_REFERENCE_MISSING');
    return result({
      actionId: action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: 'athena_life_os',
      entityId: item.id,
      inboxItem: item,
      userMessage: `Pronto: coloquei “${item.title || intent.title}” na sua Inbox para revisar depois.`,
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_life_os' });
  }
}

async function executeSaveProjectDecision(action, context) {
  const { apiBase, token, intent, projectId, sessionId } = context;
  if (intent.incomplete) {
    return result({
      actionId: action.id,
      type: action.type,
      status: 'needs_input',
      success: false,
      provider: 'none',
      errorCode: 'PROJECT_DECISION_REQUIRED',
      userMessage: intent.message,
    });
  }
  if (!projectId) {
    return result({
      actionId: action.id,
      type: action.type,
      status: 'needs_input',
      success: false,
      provider: 'none',
      errorCode: 'PROJECT_CONTEXT_REQUIRED',
      userMessage: 'Abra ou selecione o projeto em que essa decisão deve ser registrada.',
    });
  }
  try {
    const { data } = await axios.post(`${apiBase}/projects/${encodeURIComponent(projectId)}/decisions`, {
      title: intent.title,
      content: intent.content,
      source_session_id: sessionId || null,
    }, authenticatedConfig(token));
    const decision = data?.decision;
    if (!decision?.id || normalize(decision.title) !== normalize(intent.title)) {
      throw new Error('PROJECT_DECISION_REFERENCE_MISSING');
    }
    return result({
      actionId: action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: 'athena_projects',
      entityId: decision.id,
      projectId,
      decision,
      userMessage: `Pronto: registrei “${decision.title}” como decisão deste projeto.`,
    });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_projects' });
  }
}

async function executeSaveContent(action, context) {
  const { apiBase, token, intent, projectId } = context;
  if (intent.incomplete) return result({ actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'none', errorCode: 'CONTENT_REQUIRED', userMessage: intent.message });
  try {
    const { data } = await axios.post(`${apiBase}/library/items`, {
      title: intent.title,
      content: intent.content,
      summary: trimText(intent.content, 300),
      category: intent.category,
      project_id: projectId || null,
      metadata: { source: 'conversation_action' },
    }, authenticatedConfig(token));
    const item = data?.item;
    if (!item?.id) throw new Error('SAVED_CONTENT_REFERENCE_MISSING');
    return result({ actionId: action.id, type: action.type, status: 'completed', success: true, provider: 'athena_library', entityId: item.id, userMessage: `Pronto: salvei “${item.title || intent.title}” na sua biblioteca.` });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_library' });
  }
}

async function executeCamera(action, context) {
  try {
    const capture = await captureAthenaPhoto();
    if (capture?.cancelled) return result({ actionId: action.id, type: action.type, status: 'cancelled', success: false, provider: 'android_camera', errorCode: 'CAMERA_CANCELLED', userMessage: 'Tudo bem, nenhuma foto foi capturada.' });
    const items = Array.isArray(capture?.items) ? capture.items : [];
    if (!items.length) return result({ actionId: action.id, type: action.type, status: 'failed', success: false, provider: 'android_camera', errorCode: capture?.status || 'CAMERA_CAPTURE_FAILED', userMessage: 'Não consegui capturar a foto. Nada foi anexado à conversa.' });
    const captured = items[0];
    // Android keeps bytes in its private cache. Persist the opaque reference
    // before we say that the photo returned to the conversation; the WebView
    // never receives a file path or a base64 payload.
    if (IS_NATIVE_APP) {
      const uploaded = await uploadAthenaContextItem(captured, {
        token: context.token,
        projectId: context.projectId,
        sessionId: context.sessionId,
      });
      if (!uploaded.ok || !uploaded.file?.id) {
        return result({
          actionId: action.id,
          type: action.type,
          status: 'failed',
          success: false,
          provider: 'android_camera',
          errorCode: uploaded.status || 'CAMERA_ATTACHMENT_UPLOAD_FAILED',
          userMessage: 'A foto foi capturada, mas não consegui anexá-la com segurança à conversa. Não a tratei como salva.',
          pendingAttachments: items,
        });
      }
      return result({
        actionId: action.id,
        type: action.type,
        status: 'completed',
        success: true,
        provider: 'android_camera',
        entityId: uploaded.attachment?.id || uploaded.file.id,
        userMessage: 'Foto capturada e anexada a esta conversa.',
        pendingAttachments: [{
          ...captured,
          attachmentRef: { file: uploaded.file, attachment: uploaded.attachment || null },
          status: 'uploaded',
        }],
        fileIds: [uploaded.file.id],
        attachmentIds: uploaded.attachment?.id ? [uploaded.attachment.id] : [],
      });
    }
    return result({ actionId: action.id, type: action.type, status: 'completed', success: true, provider: 'browser_camera', entityId: captured?.id || null, userMessage: 'Foto capturada e pronta para ser anexada nesta conversa.', pendingAttachments: items });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'android_camera' });
  }
}

async function executeExternal(action, context) {
  try {
    const opened = await openExternal(context.intent.url);
    return opened
      ? result({ actionId: action.id, type: action.type, status: 'completed', success: true, provider: 'native_browser', entityId: context.intent.name, userMessage: `Pronto: abri ${context.intent.name} no navegador.` })
      : result({ actionId: action.id, type: action.type, status: 'failed', success: false, provider: 'native_browser', entityId: context.intent.name, errorCode: 'EXTERNAL_OPEN_FAILED', userMessage: `Não consegui abrir ${context.intent.name} no navegador.` });
  } catch (error) {
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'native_browser', entityId: context.intent.name });
  }
}

async function executeImage(action, context) {
  const prompt = imagePromptFromMessage(context.message);
  if (!prompt) {
    return result({
      actionId: action.id, type: action.type, status: 'needs_input', success: false, provider: 'athena_image_provider',
      errorCode: 'IMAGE_PROMPT_REQUIRED', userMessage: 'Que imagem você quer que eu gere? Descreva a cena, o estilo ou o objetivo.',
    });
  }
  try {
    const generated = await generateImage({
      prompt,
      projectId: context.projectId || null,
      sessionId: context.sessionId || null,
      actionId: action.id,
      idempotencyKey: `${action.id}:image`,
      apiBase: context.apiBase,
    }, context.token);
    const image = generated?.image;
    const imageUrl = generated?.image_url || image?.external_url || null;
    if (!generated?.success || !image?.id || !imageUrl) throw new Error('IMAGE_RESULT_REFERENCE_MISSING');
    return result({
      actionId: generated.actionId || action.id,
      type: action.type,
      status: 'completed',
      success: true,
      provider: generated.provider || 'athena_image_provider',
      entityId: generated.entityId || image.id,
      userMessage: generated.userMessage || 'Pronto: gerei a imagem.',
      image,
      imageUrl,
      promptUsed: generated.prompt_used || prompt,
    });
  } catch (error) {
    const remote = error?.result;
    if (remote && typeof remote === 'object') {
      return result({
        actionId: remote.actionId || action.id,
        type: action.type,
        status: remote.status || 'failed',
        success: false,
        provider: remote.provider || 'athena_image_provider',
        entityId: remote.entityId || null,
        errorCode: remote.errorCode || remote.error_code || 'IMAGE_GENERATION_FAILED',
        userMessage: remote.userMessage || remote.detail || 'Não consegui confirmar a geração da imagem.',
        retryAfterSeconds: remote.retry_after_seconds || null,
      });
    }
    return failureFrom(error, { actionId: action.id, type: action.type, provider: 'athena_image_provider' });
  }
}

async function recordActionAudit(context, actionResult) {
  if (!context.apiBase || !context.token) return;
  try {
    await axios.post(`${context.apiBase}/actions/audit`, {
      action_id: actionResult.actionId,
      type: actionResult.type,
      status: actionResult.status,
      success: actionResult.success,
      provider: actionResult.provider,
      entity_id: actionResult.entityId,
      error_code: actionResult.errorCode,
      session_id: context.sessionId || null,
    }, authenticatedConfig(context.token));
  } catch {
    // Audit availability cannot turn a verified device/server action into a
    // false failure. The operation itself remains owned by its provider.
  }
}

async function persistActionConversation(context, actionResult) {
  const persisted = await persistOperationalChat({
    apiBase: context.apiBase,
    token: context.token,
    sessionId: context.sessionId,
    userMessage: context.message,
    assistantMessage: actionResult.userMessage,
    projectId: context.projectId || null,
    responsePresentation: actionResult.responsePresentation || null,
    fileIds: actionResult.fileIds || [],
    attachmentIds: actionResult.attachmentIds || [],
  }).catch(() => null);
  return {
    ...actionResult,
    sessionId: persisted?.sessionId || context.sessionId || null,
    responseMessageId: persisted?.responseMessageId || null,
    responsePresentation: persisted?.responsePresentation || actionResult.responsePresentation || null,
  };
}

/**
 * Attempts one structured operational action. Normal conversation returns
 * null; handled actions always return a canonical ActionResult plus the
 * persisted-conversation references used by any UI surface.
 */
export async function executeAthenaOperationalAction({
  message,
  apiBase,
  token,
  sessionId = null,
  projectId = null,
  conversation = [],
} = {}) {
  const intent = detectAthenaOperationalIntent(message, { conversation, projectId });
  if (!intent) return null;
  const action = { id: actionId(), type: intent.type };
  const context = { message: trimText(message, 10_000), apiBase, token, sessionId, projectId, conversation, intent };
  if (!token || !apiBase) {
    return persistActionConversation(context, result({ actionId: action.id, type: action.type, status: 'failed', success: false, provider: 'none', errorCode: 'AUTHENTICATION_REQUIRED', userMessage: 'Entre novamente para que eu possa executar essa ação.' }));
  }

  let actionResult;
  try {
    switch (action.type) {
      case ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_ALARM:
      case ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_REMINDER:
        actionResult = await executeReminder(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_CALENDAR_EVENT:
        actionResult = await executeCalendarCreate(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.UPDATE_CALENDAR_EVENT:
        actionResult = await executeCalendarUpdate(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_TASK:
        actionResult = await executeTask(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.ASSIGN_TASK_TO_PROJECT:
        actionResult = await executeTaskProjectAssignment(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_PROJECT:
        actionResult = await executeProject(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.CREATE_ROUTINE:
        actionResult = await executeCreateRoutine(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.UPDATE_ROUTINE:
        actionResult = await executeUpdateRoutine(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.REORGANIZE_DAY:
        actionResult = await executeReorganizeDay(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.CONFIRM_DAY_PROPOSAL:
        actionResult = await executeConfirmDayProposal(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_TO_INBOX:
        actionResult = await executeSaveToInbox(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_PROJECT_DECISION:
        actionResult = await executeSaveProjectDecision(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.SAVE_CONTENT:
        actionResult = await executeSaveContent(action, context); break;
      case ATHENA_OPERATIONAL_ACTION_TYPES.TAKE_PHOTO:
        actionResult = await executeCamera(action, context); break;
    case ATHENA_OPERATIONAL_ACTION_TYPES.OPEN_EXTERNAL:
      actionResult = await executeExternal(action, context); break;
    case ATHENA_OPERATIONAL_ACTION_TYPES.GENERATE_IMAGE:
      actionResult = await executeImage(action, context); break;
      default:
        actionResult = result({ actionId: action.id, type: action.type, status: 'failed', success: false, provider: 'none', errorCode: 'ACTION_UNSUPPORTED', userMessage: 'Essa ação ainda não está disponível.' });
    }
  } catch (error) {
    actionResult = failureFrom(error, {
      actionId: action.id,
      type: action.type,
      provider: 'none',
    });
  }
  void recordActionAudit(context, actionResult);
  return persistActionConversation(context, actionResult);
}
